<template>

    
    <div>
        <b-modal :ref="'add-modal-' + targetId" :key="'time-modal-' + targetId" hide-footer centered title="Assign a task" size="sm">
      <div>
        <div style="width: 100%;">
          <p>Task</p>
          <treeselect v-model="newTaskId" :multiple="false" :options="mappedTasks" :clearable="false" style="width: 100%;">
            <template #option="{ node }">{{ node.title }}</template>
          </treeselect>
        </div>

        <!-- Per-person rows on the non-service-delivery rota: the row has no
             site of its own, so the task is placed at one or more of the
             person's sites from here. "Remaining" is every site without a
             task in this slot yet, for the "not on site, call and email
             support" kind of task that covers wherever the person is not. -->
        <div v-if="multiSite" class="mt-1" style="width: 100%;">
          <p class="mb-25">Location</p>
          <treeselect
            v-model="selectedSiteIds"
            :multiple="true"
            :options="siteOptions"
            :clearable="true"
            :flat="true"
            placeholder="Choose site(s)..."
            style="width: 100%;"
          />
          <div class="d-flex flex-wrap mt-50" style="gap: 4px;">
            <b-button
              size="sm"
              variant="outline-primary"
              :disabled="remainingSites.length === 0"
              @click="selectRemainingSites"
            >
              Remaining sites ({{ remainingSites.length }})
            </b-button>
            <b-button size="sm" variant="outline-secondary" @click="selectAllSites">
              All sites ({{ siteOptions.length }})
            </b-button>
          </div>
          <small v-for="line in occupiedSiteLines" :key="line.period" class="text-muted d-block mt-25">
            Already assigned this {{ line.period }}: {{ line.names.join(', ') }}
          </small>
          <small v-if="siteOptions.length === 0" class="text-danger d-block mt-25">
            This staff member has no active site to assign to.
          </small>
        </div>

        <!-- AM, PM or both: one shift is added per ticked period. -->
        <div class="mb-1 mt-1">
          <b-form-checkbox-group v-model="selectedPeriods">
            <b-form-checkbox value="AM">AM</b-form-checkbox>
            <b-form-checkbox value="PM">PM</b-form-checkbox>
          </b-form-checkbox-group>
        </div>

        <div v-for="p in orderedPeriods" :key="'times-' + p" class="d-flex justify-content-between mt-1" style="width: 100%;">
          <div style="width: 48%;">
            <p>{{ orderedPeriods.length > 1 ? p + ' ' : '' }}Start Time</p>
            <flat-pickr v-model="times[p].start" class="form-control" :config="pickerConfigs[p].start" />
          </div>

          <div style="width: 48%;">
            <p>{{ orderedPeriods.length > 1 ? p + ' ' : '' }}End Time</p>
            <flat-pickr v-model="times[p].end" class="form-control" :config="pickerConfigs[p].end" />
          </div>
        </div>
      </div>

      <div class="d-flex mt-3 justify-content-end">
        <b-button variant="primary" @click="saveAddChanges(targetId)">Save</b-button>
      </div>
    </b-modal>
        
        <b-button
          :class="compact ? 'assignMoreBtn' : 'popoverBtn'"
          :variant="compact ? 'flat-secondary' : 'outline-secondary'"
          :size="compact ? 'sm' : undefined"
          :title="compact ? 'Assign another site in this slot' : undefined"
          @contextmenu.prevent="$refs[targetId].open"
          @click="optionClicked(targetId)"
          v-if=" userData.status=='active'&& ($Can('add_task_to_all') || ($Can('add_task_to_department')&&userData.departmentIds.includes(departmentId)))"
        >
          <template v-if="compact">+ Add site</template>
          <template v-else>
          <span v-if="userDataSchedule.multiple_sites">

<feather-icon
icon="AlertCircleIcon"
size="12"
v-b-tooltip.hover title="User not assigned to center."
class="warning-button"

/>
</span>
          Click to assign
          </template>

  </b-button>

      <vue-context :ref="targetId" :key="'right-click-' + targetId">
        <li>
          <b-link v-for="data in menuData" :key="data.text" class="d-flex align-items-center" @click="optionClicked(targetId)">
            <feather-icon :icon="data.icon" size="16" />
            <span class="ml-75">{{ data.text }}</span>
          </b-link>
        </li>
      </vue-context>
    </div>
  </template>
  
  <script>
  import store from '@/store';
  import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
  import VueContext from 'vue-context';
  import flatPickr from 'vue-flatpickr-component';
  import Treeselect from '@riophae/vue-treeselect';
  import { INVALID_TIME_RANGE_MESSAGE, isValidTimeRange, bumpEndTime, apiErrorMessage } from '@/libs/timeRange';

  export default {
    components: {
      ToastificationContent,
      VueContext,
      flatPickr,
      Treeselect,
    },
  
    props: {
      day: Object,
      oldDay: Object,
      tasks: Array,
      targetId: String,
      value: Object,
      dayIndex: Number,
      formShow: Boolean,
      userRole: String,
      departmentId: Number,
      userId:Number,
      userDataSchedule:Object,
      dayName:String,
      period:String,
      // Per-person rows (unpublished non-service-delivery rota) only. The
      // sites the person may be placed at, the entries already in this
      // day-and-period cell (to work out which sites are still free), and
      // whether to draw the small in-cell button instead of the full one.
      sites: { type: Array, default: () => [] },
      multiSite: { type: Boolean, default: false },
      dayItems: { type: Array, default: () => [] },
      // The same day's entries in the other period, so ticking both AM and
      // PM can still tell which sites are free in both.
      otherDayItems: { type: Array, default: () => [] },
      compact: { type: Boolean, default: false },
    },
  
    data() {
      return {
        userData: JSON.parse(localStorage.getItem('userData')),
        userPermms: JSON.parse(localStorage.getItem('userPerms')),
        todayDate: new Date().toISOString().slice(0, 10),
        showPopover: false,
        showHover: false,
        showTimePicker: false,
        selectedPeriods: [this.period ?? 'AM'],
        times: this.defaultTimes(),
        pickerConfigs: {
          AM: { start: this.getPickerConfig(), end: this.getPickerConfig() },
          PM: { start: this.getPickerConfig(), end: this.getPickerConfig() },
        },
        newTaskId: null,
        mappedTasks: [],
        selectedSiteIds: [],
        menuData: [
          { icon: 'PlusIcon', text: 'Add New Task', where: 'add' },

        ],
      };
    },


    computed: {
    // Which rota this cell belongs to. Read from the route because both rota
    // pages render this same component.
    isPublishedView() {
      return this.$route.meta.publishedRota === true;
    },

    siteOptions() {
      return (this.sites || []).map((site) => {
        const dept = site.department_title;
        const showDept = dept && dept.toLowerCase() !== String(site.title || '').toLowerCase();
        return {
          id: site.id,
          label: showDept ? `${site.title} (${dept})` : site.title,
        };
      });
    },

    // Ticked periods, always AM before PM.
    orderedPeriods() {
      return ['AM', 'PM'].filter((p) => this.selectedPeriods.includes(p));
    },

    // Site ids that already hold an entry on this day, per period.
    occupiedByPeriod() {
      const siteIds = (items) => Array.from(new Set((items || [])
        .map((item) => (item && item.site_id != null ? Number(item.site_id) : null))
        .filter((id) => id !== null)));
      const clicked = this.period || 'AM';
      const other = clicked === 'AM' ? 'PM' : 'AM';
      return { [clicked]: siteIds(this.dayItems), [other]: siteIds(this.otherDayItems) };
    },

    // Sites taken in any ticked period: "remaining" means free in all of them.
    occupiedSiteIds() {
      const ids = this.orderedPeriods.flatMap((p) => this.occupiedByPeriod[p] || []);
      return Array.from(new Set(ids));
    },

    occupiedSiteLines() {
      return this.orderedPeriods
        .map((p) => ({
          period: p,
          names: (this.sites || [])
            .filter((site) => (this.occupiedByPeriod[p] || []).includes(Number(site.id)))
            .map((site) => site.title),
        }))
        .filter((line) => line.names.length > 0);
    },

    remainingSites() {
      return (this.sites || []).filter((site) => !this.occupiedSiteIds.includes(Number(site.id)));
    },

  
      localShowPopover: {
        get() {
          return this.value;
        },
        set(newValue) {
          this.$emit('input', newValue);
        },
      },
    },
  
    watch: {
      'times.AM.start'() {
        this.syncEndTime('AM');
      },

      'times.PM.start'() {
        this.syncEndTime('PM');
      },
    },
  

    methods: {
      showOldTask(day) {
        if (day.change_awaits) {
          this.$set(day, 'isHovered', true);
        }
      },
      hideOldTask(day) {
        this.$set(day, 'isHovered', false);
      },
  
      buttonStyle(day) {
        if (!day.task || !day.task.color) {
          return {};
        }
  
        const color = this.displayedColor || day.task.color;
  
        return {
          backgroundColor: color.bgColor,
          color: color.textColor,
          position: 'relative',
        };
      },
  
      getPickerConfig(minTime, maxTime) {
        return {
          static: true,
          enableTime: true,
          noCalendar: true,
          dateFormat: 'H:i',
          time_24hr: true,

        };
      },
  
      defaultTimes() {
        return {
          AM: { start: '09:00', end: '13:00' },
          PM: { start: '14:00', end: '16:00' },
        };
      },

      syncEndTime(period) {
        const t = this.times[period];
        this.pickerConfigs[period].end.minTime = t.start;
        t.end = bumpEndTime(t.start, t.end);
      },

      selectRemainingSites() {
        this.selectedSiteIds = this.remainingSites.map((site) => site.id);
      },

      selectAllSites() {
        this.selectedSiteIds = (this.sites || []).map((site) => site.id);
      },

      optionClicked(targetId) {

          // Fresh each time: the clicked period ticked, default times.
          this.selectedPeriods = [this.period || 'AM'];
          this.times = this.defaultTimes();
          this.mappedTasks = this.tasks.map((task) => ({ label: task.title, id: task.id }));
          // Fresh choice each time the modal opens. One site needs no
          // choosing; otherwise start from the sites still free in this slot,
          // which is the common case, and let the user change it.
          if (this.multiSite) {
            this.newTaskId = null;
            if (this.siteOptions.length === 1) {
              this.selectedSiteIds = [this.siteOptions[0].id];
            } else {
              this.selectedSiteIds = this.remainingSites.map((site) => site.id);
            }
          }
          // Tüm kullanıcılara görev ekleme yetkisini kontrol et
          if (this.$Can('add_task_to_all') || this.userData.departmentIds.includes(this.departmentId) ) {
            // Departman kullanıcılarına görev ekleme yetkisini ve departman eşleşmesini kontrol et
            if (this.$Can('add_task_to_department')) {
              this.$nextTick(() => {
                const modalRef = this.$refs['add-modal-' + targetId];
  
                if (modalRef) {
                  modalRef.show();
                } else {
                  console.error(`Add modal reference not found for targetId: ${targetId}`);
                }
              });
            } else {
              // Departman kullanıcılarına görev ekleme yetkisi yok
              this.showWarningToast('You do not have permission for this action.');
            }
          } else {
            // Tüm kullanıcılara görev ekleme yetkisi yok
            this.showWarningToast('You do not have permission for this action.');
          }
   
      },
  
      deleteTask(targetId) {
        // Confirm deletion
        this.$bvModal
          .msgBoxConfirm('Are you sure you want to delete this task?', {
            title: 'Confirm Deletion',
            size: 'sm',
            okVariant: 'danger',
            okTitle: 'Yes',
            cancelTitle: 'No',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
          })
          .then((value) => {
            if (value) {
              store
                .dispatch('dashboard/deleteTask', { taskId: targetId })
                .then(() => {
                  this.$toast({
                    component: ToastificationContent,
                    props: {
                      title: 'Successful',
                      text: '✔️ Task deleted successfully',
                      icon: 'ThumbsUpIcon',
                      variant: 'success',
                    },
                  });
                  this.$emit('changeTaskCompleted');
                })
                .catch((error) => {
                  console.error(error);
                  this.$toast({
                    component: ToastificationContent,
                    props: {
                      title: 'An error occurred',
                      text: 'Please try again later or contact support.',
                      icon: 'AlertCircleIcon',
                      variant: 'danger',
                    },
                  });
                });
            }
          })
          .catch((err) => {
            console.error(err);
          });
      },
  
      saveAddChanges(targetId) {
        if (!this.newTaskId) {
          this.showWarningToast('Please select a task before saving.');
          return;
        }
        if (this.orderedPeriods.length === 0) {
          this.showWarningToast('Please tick AM, PM or both.');
          return;
        }
        if (this.multiSite && this.selectedSiteIds.length === 0) {
          this.showWarningToast('Please choose at least one location.');
          return;
        }
        if (this.orderedPeriods.some((p) => !isValidTimeRange(this.times[p].start, this.times[p].end))) {
          this.showErrorMessage(INVALID_TIME_RANGE_MESSAGE);
          return;
        }
        this.addNewSchedule(targetId);
      },

      schedulePayload(period, removeConflict) {
        return {
          userData: this.userDataSchedule,
          new_task_id: this.newTaskId,
          start_time: this.times[period].start,
          end_time: this.times[period].end,
          period,
          week: this.userDataSchedule.week,
          year: this.userDataSchedule.year,
          day: this.dayName,
          removeConflict,
          published: this.isPublishedView ? 1 : 0,
          // Per-person rows name the site(s) themselves; the API then writes
          // one entry per site and skips the one-site-per-slot check.
          site_ids: this.multiSite && !removeConflict ? this.selectedSiteIds : undefined,
        };
      },

      // One add per ticked period, in turn, so each keeps its own times and
      // its own "assigned to another site" prompt.
      async addNewSchedule(targetId) {
        const periods = this.orderedPeriods;
        let added = 0;
        let created = 0;
        const skipped = [];

        try {
          for (const period of periods) {
            const response = await store.dispatch('dashboard/addNewScheduleDashboard', this.schedulePayload(period, false));
            const data = response.data || {};

            if (this.multiSite && typeof data.created === 'number') {
              created += data.created;
              const label = periods.length > 1 ? ` (${period})` : '';
              (Array.isArray(data.skipped) ? data.skipped : []).forEach((site) => skipped.push(site + label));
              continue;
            }

            if (data.message == 'Another Site') {
              const result = await this.$swal({
                title: 'The user is assigned to another site',
                text: 'User tasks in ' + data.site + (periods.length > 1 ? ' (' + period + ')' : '') + ' will be removed. Do you want to continue?',
                icon: 'success',
                showCancelButton: true,
                confirmButtonText: 'Continue',
                customClass: {
                  confirmButton: 'btn btn-primary',
                  cancelButton: 'btn btn-danger ml-1',
                },
                buttonsStyling: false,
              });
              if (!result.value) {
                continue;
              }
              await store.dispatch('dashboard/addNewScheduleDashboard', this.schedulePayload(period, true));
            }

            added++;
          }
        } catch (error) {
          this.showErrorMessage(apiErrorMessage(error, 'Please try again later or contact support.'));
          this.formShow = false;
          // Whatever went in before the failure should still show.
          if (added || created) {
            this.$emit('changeTaskCompleted');
          }
          return;
        }

        if (this.multiSite) {
          if (created === 0) {
            this.showWarningToast(
              skipped.length
                ? `Nothing added: this task is already in this slot at ${skipped.join(', ')}.`
                : 'Nothing was added.'
            );
          } else {
            const shiftsWord = created === 1 ? 'shift' : 'shifts';
            const note = skipped.length ? ` (already there at ${skipped.join(', ')})` : '';
            this.showSuccessToast(`✔️ ${created} ${shiftsWord} added${note}`);
          }
        } else if (added === 0) {
          // Every period was cancelled at the prompt: leave the modal open.
          return;
        } else {
          this.showSuccessToast('✔️ Task Update Successful');
        }

        this.hideModal(targetId);
        this.$emit('changeTaskCompleted');
      },
  
      showSuccessToast(message) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Successful',
            text: message,
            icon: 'ThumbsUpIcon',
            variant: 'success',
          },
        });
      },
  
      showWarningToast(message) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Warning',
            text: message,
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        });
      },
  
      showErrorToast() {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'An error occurred',
            text: 'Please try again later or contact support.',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        });
      },

      showErrorMessage(message) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'An error occurred',
            text: message,
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        });
      },
  
      hideModal(targetId) {
        this.$refs['time-modal-' + targetId]?.hide();
        this.$refs['add-modal-' + targetId]?.hide();
      },
  
      changeTask(newTask) {
        if (this.$Can('change_task_for_all') || this.userData.departmentIds.includes(this.departmentId)) {
          if (this.$Can('change_task_for_department')  || this.userId == this.userData.id) {
            this.$emit('formShow', true);
            store
              .dispatch('dashboard/updateSchedule', { oldTask: this.value, newTask })
              .then(() => {
                this.showSuccessToast('✔️ Task Update Successful');
                this.$root.$emit('bv::hide::popover');
                this.$emit('changeTaskCompleted');
              })
              .catch(() => {
                this.showErrorToast();
                this.$emit('formShow', false);
              });
          
          }
          
          else {
            this.showWarningToast('You do not have permission for this action.');
          }
        } else {
          this.showWarningToast('You do not have permission for this action.');
        }
      },
    },
  };
  </script>
  
  <style scoped>
  .time-picker-container {
    display: flex;
    align-items: center;
  }
  .time-picker-container > * {
    margin-right: 10px;
  }
  .popoverBtn {
    height: 36px;
    font-size: 0.8rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100px;
    width: 100%;
    border: none;
    cursor: pointer;
    width: calc(100% - 40px);
  }
  .popoverBtn:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
    transition: box-shadow 0.3s ease-in-out !important;
  }
  /* The in-cell button under existing chips on a per-person row. */
  .assignMoreBtn {
    font-size: 0.7rem;
    padding: 2px 8px;
    margin-top: 2px;
    border: 1px dashed #b8b8b8;
    width: calc(100% - 40px);
  }
  .time-info-container {
    display: flex;
    justify-content: space-between;
    border-top: solid 1px #666;
  }
  .time-info {
    display: inline-block;
  }
  
  .strikethrough {
    text-decoration: line-through;
  }
  
  @media screen and (min-width: 768px) {
    .popoverBtn {
      min-width: 150px;
      max-width: 150px;
    }
  }
  @media screen and (min-width: 992px) {
    .popoverBtn {
      min-width: 125px;
      max-width: 125px;
    }
  }
  @media screen and (min-width: 1900px) {
    .popoverBtn {
      min-width: 220px;
      max-width: 220px;
    }
  }
  </style>
  <style lang="scss">
  @import '~vue-context/dist/css/vue-context.css';
  @import '@core/scss/vue/libs/vue-flatpicker.scss';
  </style>
  